// =============================================================================
// Email Express — body paragraph segmentation
// -----------------------------------------------------------------------------
// Single source of truth for turning a body-copy string into the small set of
// short paragraphs the renderers display (preview + sent MJML stay in parity).
//
// The templated generator separates body paragraphs with blank lines ("\n\n"),
// but the AI brand-voice rewrite that runs afterward can collapse the body into
// one continuous block with the blank lines stripped. When that happens the
// renderers would treat the whole body as a single (over-emphasized) lead-in
// paragraph — a wall of text. This helper re-segments such a collapsed block on
// sentence boundaries (never mid-sentence) so it reads as a few paragraphs.
// =============================================================================

// Only re-segment a single un-broken block once it is long enough to warrant it.
const MIN_RESEGMENT_CHARS = 220;

// Split text into sentences without cutting mid-sentence. The lookbehind/
// lookahead pair only breaks at whitespace that follows a sentence terminator
// AND precedes a capital letter / digit / opening quote — so it leaves
// abbreviations ("U.S. citizens") and decimals ("9.99 each") intact.
function splitSentences(text: string): string[] {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return [];
  return normalized
    .split(/(?<=[.!?])\s+(?=["'“‘([]?[A-Z0-9])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Distribute sentences into `n` contiguous, roughly balanced paragraphs.
function distribute(sentences: string[], n: number): string[] {
  const groups: string[] = [];
  const per = Math.ceil(sentences.length / n);
  for (let i = 0; i < sentences.length; i += per) {
    const chunk = sentences.slice(i, i + per).join(" ").trim();
    if (chunk) groups.push(chunk);
  }
  return groups;
}

// Re-segment a single un-broken block into 2–3 short paragraphs. Short blocks
// (few sentences / not long) are returned untouched as a single paragraph.
function segmentSingleBlock(block: string): string[] {
  const trimmed = block.trim();
  if (!trimmed) return [];
  const sentences = splitSentences(trimmed);
  if (sentences.length < 3 || trimmed.length < MIN_RESEGMENT_CHARS) {
    return [trimmed];
  }
  const targetParas = sentences.length >= 5 ? 3 : 2;
  const segmented = distribute(sentences, targetParas);
  return segmented.length ? segmented : [trimmed];
}

/**
 * Split a body-copy string into the paragraphs to render.
 * - Honors explicit blank-line ("\n\n") paragraph breaks when present.
 * - Falls back to sentence-boundary re-segmentation for a long single block
 *   (the collapsed-rewrite safety net), never splitting mid-sentence.
 */
export function segmentEmailBody(body: string | null | undefined): string[] {
  const raw = (body || "").trim();
  if (!raw) return [];
  const explicit = raw
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (explicit.length > 1) return explicit;
  return segmentSingleBlock(explicit[0] ?? raw);
}
